import { Avatar } from "@mui/material";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";

import AuthLogout from "@/Components/auth/AuthLogout";
import { CityIcon } from "@/Components/Icons/CityIcon";
import { LogoutIcon } from "@/Components/Icons/LogoutIcon";
import { ProfileIcon } from "@/Components/Icons/ProfileIcon";
import SidebarIcons from "@/Components/Icons/SidebarIcons";
import { routingConstants } from "@/Constants/routingConstants";
import {
  toggleClientBranchesModal,
  toggleMobileDrawer,
} from "@/reducers/clientcommon";
import useIsMobile from "@/utilities/hooks/useIsMobile";

import NoAccessButtonWrapper from "../../UserRoleAccess/NoAccessButtonWrapper";

/**
 * SidebarProfile component that displays user and client information in the sidebar.
 *
 * @return {JSX.Element} The JSX for the SidebarProfile component
 */
const SidebarProfile = () => {
  const { clientUserAllPermissions } = useSelector((state) => state.session);
  const { user } = useSelector((state) => state.clientProfile);
  const { activePage, sideBarCollapsed } = useSelector(
    (state) => state.clientCommon,
  );

  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // On mobile, sidebar should always render in expanded state
  const effectiveCollapsed = isMobile ? false : sideBarCollapsed;

  const { original_user, is_headoffice } = user;
  // Component to handle logout button disabling
  const { logout, isLoggingOut } = AuthLogout();

  // Close drawer on profile link navigation
  const handleProfileNav = () => {
    if (isMobile) {
      dispatch(toggleMobileDrawer(false));
      setMobileMenuOpen(false);
    }
  };

  const getUserAccessLink = () => {
    return !clientUserAllPermissions ||
      clientUserAllPermissions.is_allow_to_users_and_roles_list !== 0 ? (
      <Link
        to={routingConstants.clientRole}
        onClick={handleProfileNav}
        className={`transition-linear flex h-[45px] items-center gap-4 px-4 text-txtColor hover:bg-lightblue hover:text-primary ${
          activePage === "clientusers"
            ? "bg-primary-100 !text-primary"
            : `text-txtColor`
        }`}
      >
        <div className="items transition-linear relative flex w-full justify-start group-hover:gap-4">
          {activePage == "clientusers" ? (
            <SidebarIcons type={"clientUserAccessActive"} />
          ) : (
            <SidebarIcons type={"clientUserAccess"} />
          )}
          <span
            className={`transition-linear absolute left-[40px] top-[2px] inline-block text-[14px] font-semibold ${isMobile ? "" : "w-0"} overflow-hidden whitespace-nowrap break-keep group-hover:w-[calc(100%-48px)]`}
          >
            User Access
          </span>
        </div>
      </Link>
    ) : (
      <NoAccessButtonWrapper
        key={"userAccess"}
        rootClass=""
        iconClass="w-[22px] h-[22px] text-primary"
        _checkPermission={["is_allow_to_users_and_roles_list"]}
      >
        <div
          className={`transition-linear flex h-[45px] items-center gap-4 px-4 text-txtColor hover:bg-lightblue hover:text-primary ${
            activePage === "clientusers"
              ? "bg-primary-100 !text-primary"
              : `text-txtColor`
          }`}
        >
          <div className="items transition-linear relative flex w-full justify-start group-hover:gap-4">
            {activePage == "clientusers" ? (
              <SidebarIcons type={"clientUserAccessActive"} />
            ) : (
              <SidebarIcons type={"clientUserAccess"} />
            )}
            <span
              className={`transition-linear absolute left-[40px] top-[2px] inline-block text-[14px] font-semibold ${isMobile ? "" : "w-0"} overflow-hidden whitespace-nowrap break-keep group-hover:w-[calc(100%-48px)]`}
            >
              User Access
            </span>
          </div>
        </div>
      </NoAccessButtonWrapper>
    );
  };

  // Shared profile menu content (used in both mobile + desktop)
  const profileMenuItems = (
    <ul className="w-full">
      <li>
        <Link
          to={routingConstants.profile}
          onClick={handleProfileNav}
          className="transition-linear flex h-[45px] items-center gap-4 px-4 text-txtColor hover:rounded-[0.5rem_0.5rem_0rem_0rem] hover:bg-lightblue hover:text-primary"
        >
          <ProfileIcon />
          <span className="ml-[5.3px] text-[14px] font-semibold leading-[normal]">
            My Profile
          </span>
        </Link>
      </li>
      <li>{getUserAccessLink()}</li>
      <li>
        <button
          onClick={() => {
            handleProfileNav();
            logout();
          }}
          disabled={isLoggingOut}
          className={`transition-linear flex h-[45px] w-full select-none items-center gap-4 px-4 text-txtColor hover:rounded-[0rem_0rem_0.5rem_0.5rem] hover:bg-lightblue hover:text-primary ${isLoggingOut ? "pointer-events-none" : "cursor-pointer"}`}
        >
          <LogoutIcon />
          <span className="ml-[1px] text-[14px] font-semibold leading-[normal]">
            {isLoggingOut ? "Logging out" : "Logout"}
          </span>
        </button>
      </li>
    </ul>
  );

  return (
    <div
      className={`SidebarProfile_Wrapper transition-linear flex w-full flex-col gap-4 bg-white ${effectiveCollapsed ? "px-4" : "px-2"} pt-2 group-hover:px-2`}
    >
      <button
        className={`tour-applocum-branch-management group/item transition-linear relative ${effectiveCollapsed ? "" : "p-[11px_16px] shadow-[0px_1px_9px_0px_rgba(0,0,0,0.10)]"} !focus:outline-none flex h-[56px] min-w-[56px] items-center gap-4 rounded-lg bg-lightblueBG text-primary !outline-none group-hover:p-[11px_16px] group-hover:shadow-[0px_1px_9px_0px_rgba(0,0,0,0.10)] ${is_headoffice == 1 || original_user != "" ? "cursor-pointer" : "cursor-default"}`}
        onClick={() => {
          is_headoffice == 1 || original_user != ""
            ? dispatch(toggleClientBranchesModal(true))
            : dispatch(toggleClientBranchesModal(false));
        }}
      >
        <div
          className={`duration-00 grid h-[56px] place-items-center transition-all ease-linear group-hover:min-w-[22px] ${effectiveCollapsed ? "min-w-[56px]" : "!min-w-[22px]"}`}
        >
          <CityIcon />
        </div>
        <div className="flex w-[calc(100%-32px-52px)] grow flex-col !justify-start overflow-hidden">
          <span className="truncate text-[14px] font-semibold leading-[normal] text-primary">
            {user?.client_branch_name || "-"}
          </span>
        </div>
      </button>
      <div
        className={`group/item transition-linear relative flex items-center gap-3 rounded-lg ${effectiveCollapsed ? "" : "p-[11px_16px] shadow-[0px_1px_9px_0px_rgba(0,0,0,0.10)]"} tour-appcloum-my-profile-clientsidebar tour-client-role-user-clientsidebar group-hover:p-[11px_16px] group-hover:shadow-[0px_1px_9px_0px_rgba(0,0,0,0.10)] ${isMobile ? "cursor-pointer" : ""}`}
        onClick={
          isMobile ? () => setMobileMenuOpen((prev) => !prev) : undefined
        }
      >
        <Avatar
          alt={user?.firstname ? user.firstname : ""}
          sx={{ width: 52, height: 52 }}
          src={user.profile_image}
          className="capitalize"
        />
        <div className="transition-linear grow overflow-hidden group-hover:w-[calc(100%-32px-52px)]">
          <h4 className="select-none truncate whitespace-nowrap text-base font-medium capitalize leading-[normal] text-txtDark">
            {user?.firstname ? user.firstname : ""}{" "}
            {user?.lastname ? user.lastname : ""}
          </h4>
          <span className="truncate whitespace-nowrap text-[14px] font-normal capitalize leading-[normal] text-txtColor">
            {user?.client_postion_name
              ? user.client_postion_name
              : user.client_manager_name}
          </span>
        </div>

        {/* Desktop: hover flyout */}
        {!isMobile && (
          <div
            className={`transition-linear invisible absolute bottom-0 left-full ml-3 flex w-[225px] translate-y-3 flex-col items-center gap-3 rounded-lg bg-white opacity-0 shadow-[0px_1px_9px_0px_#00000026] group-hover/item:visible group-hover/item:translate-y-0 group-hover/item:opacity-100`}
          >
            {profileMenuItems}
          </div>
        )}
      </div>

      {/* Mobile: inline toggle list */}
      {isMobile && mobileMenuOpen && (
        <div className="rounded-lg bg-white shadow-[0px_1px_9px_0px_#00000026]">
          {profileMenuItems}
        </div>
      )}
    </div>
  );
};

export default SidebarProfile;
